import { Fragment, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardBody, CardHeader, Col, Input, Label, Row } from 'reactstrap';
import { H3 } from '../../../Themes/Components/AbstractElements';
import ReactApexChart from 'react-apexcharts';
import DataTable from 'react-data-table-component';
import { ArrowUp, ArrowDown } from 'react-feather';
import { intervalToDuration, format, formatDuration } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import Select from 'react-select';
import ReactDatePicker from 'react-datepicker';
import ReactInputMask from 'react-input-mask';
import { FormControl } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import Loading from '../../../Themes/Components/Loading';
import ConfiguracaoUsuariosService from '../../../Services/Configuracao/ConfiguracaoUsuariosService';
import { toast } from 'react-toastify';

const FILTROS_INICIAIS = {
  prazo: null,
  periodo: null,
  periodo_inicio: null,
  periodo_fim: null,
  numero: '',
  ref_cliente: '',
  responsavel: null,
  justificativa: null,
};

const EventosDetalheChart = ({ data, configId }) => {
  const [tabelaData, setTabelaData] = useState({ rows: [], total: 0 });
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [labelSelecionado, setLabelSelecionado] = useState(null);
  const [filterKey, setFilterKey] = useState(0);
  const [chartKey, setChartKey] = useState(0);

  const [dadosFormatados, setDadosFormatados] = useState({
    series: [],
    options: { labels: [] },
  });

  const filtrosRef = useRef(filtros);
  const labelSelecionadoRef = useRef(labelSelecionado);
  filtrosRef.current = filtros;
  labelSelecionadoRef.current = labelSelecionado;

  const fetchTabela = useCallback(
    async (page, currentFiltros, currentLabel) => {
      if (!data?._grafico) return;
      setTableLoading(true);
      try {
        const params = { page };
        if (currentFiltros.prazo) params.prazo = currentFiltros.prazo.value;
        if (currentFiltros.periodo) params.periodo = currentFiltros.periodo.value;
        if (currentFiltros.periodo_inicio) params.periodo_inicio = format(currentFiltros.periodo_inicio, 'yyyy-MM-dd');
        if (currentFiltros.periodo_fim) params.periodo_fim = format(currentFiltros.periodo_fim, 'yyyy-MM-dd');
        if (currentFiltros.numero) params.numero = currentFiltros.numero;
        if (currentFiltros.ref_cliente) params.ref_cliente = currentFiltros.ref_cliente;
        if (currentFiltros.responsavel) params.responsavel = currentFiltros.responsavel.value;
        if (currentFiltros.justificativa) params.justificativa = currentFiltros.justificativa.value;
        if (currentLabel) params.label = currentLabel;

        const response = await ConfiguracaoUsuariosService.getGrafico(data._grafico, configId, params);
        setTabelaData({
          rows: response.tabela || [],
          total: response.paginacao?.total ?? response.tabela?.length ?? 0,
        });
        setCurrentPage(page);
      } catch (error) {
        toast.error('Erro ao carregar os dados da tabela.');
      } finally {
        setTableLoading(false);
      }
    },
    [data, configId],
  );

  const fetchTabelaRef = useRef(fetchTabela);
  fetchTabelaRef.current = fetchTabela;

  useEffect(() => {
    if (data?.tabela) {
      setTabelaData({
        rows: data.tabela,
        total: data.paginacao?.total ?? data.tabela.length,
      });
    }
  }, [data]);

  useEffect(() => {
    if (data && data.data) {
      const valores = data.data.data.reduce((acc, val, i) => {
        if (val > 0) acc.push(i);
        return acc;
      }, []);

      const series = valores.map((i) => data.data.data[i]);
      const labels = valores.map((i) => data.data.labels[i]);
      const colors = valores.map((i) => data.data.colors[i]);

      const dados = {
        series,
        options: {
          chart: {
            events: {
              dataPointSelection: (event, chartContext, config) => {
                const idx = config.dataPointIndex ?? config.seriesIndex;
                if (idx < 0 || idx >= labels.length) return;
                const clickedLabel = labels[idx];
                if (!clickedLabel) return;
                const newLabel = labelSelecionadoRef.current === clickedLabel ? null : clickedLabel;
                labelSelecionadoRef.current = newLabel;
                setLabelSelecionado(newLabel);
                fetchTabelaRef.current(1, filtrosRef.current, newLabel);
                setCurrentPage(1);
              },
            },
          },
          labels,
          colors,
          plotOptions: {
            pie: {
              dataLabels: { offset: -10, minAngleToShowLabel: 10 },
            },
          },
          dataLabels: {
            enabled: true,
            formatter: function (val, opts) {
              const value = opts.w.globals.series[opts.seriesIndex];
              return value + ' (' + val.toFixed(1) + '%)';
            },
          },
          legend: { position: 'bottom' },
          responsive: [
            {
              breakpoint: 480,
              options: {
                chart: { width: 200 },
                legend: { position: 'bottom' },
              },
            },
          ],
        },
      };

      setDadosFormatados(dados);
    }
  }, [data]);

  const opcoesSelect = useMemo(() => {
    const filtro = data?.filtros || {};
    const toOptions = (opts) => Object.keys(opts || {}).map((v) => ({ value: v, label: opts[v] }));
    return {
      prazo: toOptions(filtro.prazo),
      periodo: toOptions(filtro.periodo),
      responsavel: toOptions(filtro.responsavel),
      justificativa: toOptions(filtro.justificativa),
    };
  }, [data?.filtros]);

  const handleBuscar = () => {
    if (filtros.periodo?.value === 'personalizado' && (!filtros.periodo_inicio || !filtros.periodo_fim)) {
      toast.warning('Informe a data de início e a data de fim para o período personalizado.');
      return;
    }
    fetchTabela(1, filtros, labelSelecionado);
    setCurrentPage(1);
  };

  const handleLimpar = () => {
    setFiltros(FILTROS_INICIAIS);
    setLabelSelecionado(null);
    setCurrentPage(1);
    setFilterKey((k) => k + 1);
    setChartKey((k) => k + 1);
    fetchTabela(1, FILTROS_INICIAIS, null);
  };

  const columns = [
    {
      name: '',
      width: '48px',
      center: true,
      cell: (row) =>
        row.dentro_prazo ? <ArrowUp size={18} color='#22BAA0' /> : <ArrowDown size={18} color='#f25656' />,
    },
    {
      name: 'Tempo',
      center: true,
      cell: (row) => {
        if (!row.tempo) return '-';
        const duration = intervalToDuration({ start: 0, end: row.tempo });
        return formatDuration(duration, { locale: ptBR, format: ['years', 'months', 'days', 'hours', 'minutes'] });
      },
    },
    {
      name: 'Número',
      center: true,
      selector: (row) => row.numero,
    },
    {
      name: <div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Ref. Cliente</div>,
      center: true,
      selector: (row) => row.ref_cliente || '-',
    },
    {
      name: <div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Responsável pelo Atraso</div>,
      center: true,
      selector: (row) => row.responsavel_atraso || '-',
    },
    {
      name: <div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Justificativa do Atraso</div>,
      grow: 2,
      center: true,
      selector: (row) => row.justificativa_atraso || '-',
    },
  ];

  const semRegistros = dadosFormatados.series.every((v) => v === 0);
  const isPersonalizado = filtros.periodo?.value === 'personalizado';

  return (
    <Fragment>
      <Card>
        <CardHeader className='pb-0'>
          <H3>{data?.titulo}</H3>
        </CardHeader>
        <CardBody>
          {semRegistros ? (
            <p style={{ textAlign: 'center', fontSize: '14px', color: 'black' }}>Nenhum registro encontrado.</p>
          ) : (
            <Row>
              <Col md='3' className='d-flex align-items-center'>
                <div className='medium-chart' style={{ width: '100%' }}>
                  <div className='recent-chart' id='recent-chart'>
                    <ReactApexChart
                      key={chartKey}
                      options={dadosFormatados.options}
                      series={dadosFormatados.series}
                      type='pie'
                      height={400}
                    />
                  </div>
                  {labelSelecionado && (
                    <p className='text-center mt-1 mb-0' style={{ fontSize: '13px', color: '#555' }}>
                      Fatia selecionada: <strong>{labelSelecionado}</strong>
                    </p>
                  )}
                </div>
              </Col>

              <Col md='9'>
                <Fragment key={filterKey}>
                  <div className='row mb-2'>
                    <div className='col-sm-2'>
                      <Label>Prazo</Label>
                      <Select
                        options={opcoesSelect.prazo}
                        placeholder='Todos'
                        isClearable
                        value={filtros.prazo}
                        onChange={(opt) => setFiltros((prev) => ({ ...prev, prazo: opt }))}
                      />
                    </div>

                    <div className='col-sm-2'>
                      <Label>Período</Label>
                      <Select
                        options={opcoesSelect.periodo}
                        placeholder='Todos'
                        isClearable
                        value={filtros.periodo}
                        onChange={(opt) =>
                          setFiltros((prev) => ({
                            ...prev,
                            periodo: opt,
                            periodo_inicio: null,
                            periodo_fim: null,
                          }))
                        }
                      />
                    </div>

                    {isPersonalizado && (
                      <>
                        <div className='col-sm-1'>
                          <Label>Início</Label>
                          <FormControl
                            as={ReactDatePicker}
                            autoComplete='off'
                            selected={filtros.periodo_inicio}
                            onChange={(date) => setFiltros((prev) => ({ ...prev, periodo_inicio: date }))}
                            customInput={
                              <ReactInputMask mask='99/99/9999'>
                                {(inputProps) => (
                                  <input {...inputProps} className='form-control datetimepicker-input digits' />
                                )}
                              </ReactInputMask>
                            }
                            locale={ptBR}
                            dateFormat='dd/MM/yyyy'
                            className='form-control datetimepicker-input digits'
                          />
                        </div>
                        <div className='col-sm-1'>
                          <Label>Fim</Label>
                          <FormControl
                            as={ReactDatePicker}
                            autoComplete='off'
                            selected={filtros.periodo_fim}
                            onChange={(date) => setFiltros((prev) => ({ ...prev, periodo_fim: date }))}
                            customInput={
                              <ReactInputMask mask='99/99/9999'>
                                {(inputProps) => (
                                  <input {...inputProps} className='form-control datetimepicker-input digits' />
                                )}
                              </ReactInputMask>
                            }
                            locale={ptBR}
                            dateFormat='dd/MM/yyyy'
                            className='form-control datetimepicker-input digits'
                          />
                        </div>
                      </>
                    )}

                    <div className='col-sm-2'>
                      <Label>Número</Label>
                      <Input
                        autoComplete='off'
                        value={filtros.numero}
                        onChange={(e) => setFiltros((prev) => ({ ...prev, numero: e.target.value }))}
                      />
                    </div>

                    <div className='col-sm-2'>
                      <Label>Ref. Cliente</Label>
                      <Input
                        autoComplete='off'
                        value={filtros.ref_cliente}
                        onChange={(e) => setFiltros((prev) => ({ ...prev, ref_cliente: e.target.value }))}
                      />
                    </div>

                    <div className='col-sm-2'>
                      <Label>Responsável</Label>
                      <Select
                        options={opcoesSelect.responsavel}
                        placeholder='Todos'
                        isClearable
                        value={filtros.responsavel}
                        onChange={(opt) => setFiltros((prev) => ({ ...prev, responsavel: opt }))}
                      />
                    </div>

                    <div className='col-sm-2'>
                      <Label>Justificativa</Label>
                      <Select
                        options={opcoesSelect.justificativa}
                        placeholder='Todas'
                        isClearable
                        empty='Selecione'
                        value={filtros.justificativa}
                        onChange={(opt) => setFiltros((prev) => ({ ...prev, justificativa: opt }))}
                      />
                    </div>

                    <div className='mt-4 d-flex align-items-end gap-2 ms-auto' style={{ width: 'fit-content' }}>
                      <Button className='btn-primary' onClick={handleBuscar}>
                        Buscar
                      </Button>

                      <Button className='btn-light' onClick={handleLimpar}>
                        Limpar
                      </Button>
                    </div>
                  </div>
                </Fragment>

                <DataTable
                  columns={columns}
                  data={tabelaData.rows}
                  striped
                  responsive
                  highlightOnHover
                  progressPending={tableLoading}
                  progressComponent={<Loading />}
                  pagination
                  paginationServer
                  paginationPerPage={10}
                  paginationTotalRows={tabelaData.total}
                  paginationDefaultPage={currentPage}
                  paginationComponentOptions={{
                    noRowsPerPage: true,
                    rangeSeparatorText: 'de',
                  }}
                  onChangePage={(page) => fetchTabela(page, filtros, labelSelecionado)}
                  noDataComponent={
                    <p style={{ padding: '16px', color: '#666', fontSize: '14px' }}>Nenhum registro encontrado.</p>
                  }
                />
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default EventosDetalheChart;
